import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Shield, ArrowRight, Funnel, MagnifyingGlass } from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { formatDate } from "../lib/format";
import { buildDefaultNdaSubject } from "../lib/ndaSubject";
import PageHeader from "../components/layout/PageHeader";
import EmptyState from "../components/EmptyState";
import CopyCodeButton from "../components/CopyCodeButton";
import StatusBadge from "../components/StatusBadge";
import RoleGuard from "../components/RoleGuard";
import SearchableSelect from "../components/SearchableSelect";
import OwnershipToggle from "../components/OwnershipToggle";
import OwnerCell from "../components/OwnerCell";
import FormDialog, { FormDialogCancel } from "../components/FormDialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";

const STATUS_OPTIONS = [
    "Draft", "Pending Approval", "Approved", "Rejected",
    "Sent", "Signed", "Active", "Expired", "Cancelled",
];
const NDA_TYPES = ["Data", "Software"];
const STATUS_BADGE_MAP = {
    "Draft": { status: "Draft" },
    "Pending Approval": { status: "Open", className: "!bg-amber-50 !text-amber-800 !border-amber-200" },
    "Approved": { status: "Locked", className: "!bg-emerald-50 !text-emerald-800 !border-emerald-200" },
    "Rejected": { status: "Unpaid" },
    "Sent": { status: "Open", className: "!bg-blue-50 !text-blue-700 !border-blue-200" },
    "Signed": { status: "Active", className: "!bg-teal-50 !text-teal-800 !border-teal-200" },
    "Active": { status: "Active" },
    "Expired": { status: "Unpaid", className: "!bg-slate-100 !text-slate-700 !border-slate-200" },
    "Cancelled": { status: "Cancelled" },
};

function _todayISO() {
    return new Date().toISOString().slice(0, 10);
}
function _addYearsISO(days) {
    const d = new Date();
    d.setFullYear(d.getFullYear() + days);
    return d.toISOString().slice(0, 10);
}

function NewNDADialog({ clients, templates, open, onOpenChange, onCreated }) {
    const blank = {
        nda_type: "Data",
        subject: "",
        // true → user has manually edited Subject; stop auto-filling.
        // Flipped back to false when the user clears the field completely
        // (allowing the default to regenerate from the current NDA Type +
        // Party Name).
        subject_overridden: false,
        purpose: "",
        nda_date: _todayISO(),
        effective_date: _todayISO(),
        valid_until: _addYearsISO(5),
        contact_person: "",
        remarks: "",
        template_id: "",
        // Party mode
        party_mode: "existing", // "existing" | "free_form"
        client_id: "",
        party_name: "",
        party_legal_name: "",
        p_email: "",
        p_phone: "",
        p_address1: "",
        p_city: "",
        p_state: "",
        p_country: "India",
        p_pin: "",
    };
    const [form, setForm] = useState(blank);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (open) setForm(blank);
    }, [open]);

    const eligibleTemplates = useMemo(
        () => templates.filter((t) => t.nda_type === form.nda_type && t.is_active !== false),
        [templates, form.nda_type],
    );

    // Derive the current party name for auto-subject purposes:
    //   existing mode → the selected client's display name
    //   free_form mode → the manually-entered party name field
    const resolvedPartyName = useMemo(() => {
        if (form.party_mode === "existing") {
            const c = clients.find((x) => x.id === form.client_id);
            return (c?.name || "").trim();
        }
        return (form.party_name || "").trim();
    }, [form.party_mode, form.client_id, form.party_name, clients]);

    const defaultSubject = useMemo(
        () => buildDefaultNdaSubject(form.nda_type, resolvedPartyName),
        [form.nda_type, resolvedPartyName],
    );

    // Auto-sync Subject whenever the default recomputes AND the user has
    // not manually overridden it.  If default is empty (party not yet
    // known) leave the field untouched — that's the pre-fill state.
    useEffect(() => {
        if (form.subject_overridden) return;
        if (!defaultSubject) return;
        if (form.subject === defaultSubject) return;
        setForm((f) => ({ ...f, subject: defaultSubject }));
    }, [defaultSubject, form.subject_overridden]);

    const submit = async () => {
        // Subject: prefer explicit form value; if the user has NOT overridden
        // and the auto-default is computable, use that.  Backend still
        // enforces non-blank Subject — we skip the client-side toast so
        // ops never see "Subject is required" when the system already has
        // enough information to auto-generate it.
        const effectiveSubject = (
            form.subject_overridden
                ? (form.subject || "")
                : ((form.subject || defaultSubject || ""))
        ).trim();
        if (!form.nda_date || !form.effective_date || !form.valid_until) {
            toast.error("Pick NDA date, effective date and validity");
            return;
        }
        const party = form.party_mode === "existing"
            ? { client_id: form.client_id, contact_person: form.contact_person }
            : {
                party_name: form.party_name.trim(),
                party_legal_name: (form.party_legal_name || "").trim() || undefined,
                contact_person: form.contact_person || undefined,
                contact_email: form.p_email || undefined,
                contact_phone: form.p_phone || undefined,
                address_line1: form.p_address1 || undefined,
                city: form.p_city || undefined,
                state_name: form.p_state || undefined,
                country: form.p_country || undefined,
                pin_code: form.p_pin || undefined,
            };
        if (form.party_mode === "existing" && !form.client_id) {
            toast.error("Pick an existing client, or switch to Free-form party");
            return;
        }
        if (form.party_mode === "free_form" && !form.party_name.trim()) {
            toast.error("Enter the party name");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/billing/nda/create", {
                nda_type: form.nda_type,
                party,
                subject: effectiveSubject,
                nda_date: form.nda_date,
                effective_date: form.effective_date,
                valid_until: form.valid_until,
                purpose: form.purpose || undefined,
                template_id: form.template_id || undefined,
                contact_person: form.contact_person || undefined,
                remarks: form.remarks || undefined,
            });
            toast.success(`NDA ${data.nda_number} created`);
            onOpenChange(false);
            onCreated?.(data);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to create NDA"));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <FormDialog
            open={open}
            onOpenChange={onOpenChange}
            title="New NDA"
            description="Create a Draft Non-Disclosure Agreement.  This document covers evaluation-only disclosure and does NOT grant any commercial or licensing right."
            testId="nda-new-dialog"
            widthClass="max-w-3xl"
        >
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <Label>NDA Type</Label>
                    <select
                        value={form.nda_type}
                        onChange={(e) => setForm((f) => ({ ...f, nda_type: e.target.value, template_id: "" }))}
                        className="mt-1 w-full h-9 px-3 text-sm border border-slate-200 rounded-md bg-white"
                        data-testid="nda-new-type"
                    >
                        {NDA_TYPES.map((t) => <option key={t} value={t}>{t} NDA</option>)}
                    </select>
                </div>
                <div>
                    <Label>Template (optional)</Label>
                    <select
                        value={form.template_id}
                        onChange={(e) => setForm((f) => ({ ...f, template_id: e.target.value }))}
                        className="mt-1 w-full h-9 px-3 text-sm border border-slate-200 rounded-md bg-white"
                        data-testid="nda-new-template"
                    >
                        <option value="">Default (latest active)</option>
                        {eligibleTemplates.map((t) => (
                            <option key={t.id} value={t.id}>{t.template_name} v{t.version}</option>
                        ))}
                    </select>
                </div>
                <div className="col-span-2">
                    <Label>Subject</Label>
                    <Input
                        value={form.subject}
                        onChange={(e) => {
                            const val = e.target.value;
                            setForm((f) => {
                                // Empty → drop the override so the default
                                // regenerates from NDA Type + Party Name.
                                if (val.trim() === "") {
                                    return { ...f, subject: "", subject_overridden: false };
                                }
                                // Diverged from the current default → user override.
                                if (val !== defaultSubject) {
                                    return { ...f, subject: val, subject_overridden: true };
                                }
                                return { ...f, subject: val };
                            });
                        }}
                        placeholder={
                            defaultSubject
                                ? defaultSubject
                                : "Auto-generated from NDA Type + Party Name"
                        }
                        data-testid="nda-new-subject"
                    />
                    <div className="text-[11px] text-slate-500 mt-1" data-testid="nda-new-subject-hint">
                        {form.subject_overridden
                            ? "Custom subject — clear the field to restore the auto-generated default."
                            : defaultSubject
                                ? "Auto-generated. Edit to override; clear to regenerate."
                                : "Auto-generates once NDA Type and Party Name are set."}
                    </div>
                </div>
                <div>
                    <Label>NDA Date *</Label>
                    <Input type="date" value={form.nda_date} onChange={(e) => setForm((f) => ({ ...f, nda_date: e.target.value }))} data-testid="nda-new-date" />
                </div>
                <div>
                    <Label>Effective Date *</Label>
                    <Input type="date" value={form.effective_date} onChange={(e) => setForm((f) => ({ ...f, effective_date: e.target.value }))} data-testid="nda-new-effective" />
                </div>
                <div>
                    <Label>Valid Until *</Label>
                    <Input type="date" value={form.valid_until} onChange={(e) => setForm((f) => ({ ...f, valid_until: e.target.value }))} data-testid="nda-new-validity" />
                </div>
                <div>
                    <Label>Contact Person</Label>
                    <Input value={form.contact_person} onChange={(e) => setForm((f) => ({ ...f, contact_person: e.target.value }))} data-testid="nda-new-contact" />
                </div>

                {/* Party selection */}
                <div className="col-span-2 border-t border-slate-200 pt-4">
                    <div className="flex items-center gap-4 mb-2">
                        <Label className="!mb-0">Party / Counterparty</Label>
                        <div className="inline-flex rounded-md border border-slate-200 overflow-hidden text-xs" data-testid="nda-party-mode-toggle">
                            <button
                                type="button"
                                onClick={() => setForm((f) => ({ ...f, party_mode: "existing" }))}
                                className={`px-3 py-1 ${form.party_mode === "existing" ? "bg-slate-900 text-white" : "text-slate-700"}`}
                                data-testid="nda-party-existing-btn"
                            >
                                Existing Client
                            </button>
                            <button
                                type="button"
                                onClick={() => setForm((f) => ({ ...f, party_mode: "free_form" }))}
                                className={`px-3 py-1 ${form.party_mode === "free_form" ? "bg-slate-900 text-white" : "text-slate-700"}`}
                                data-testid="nda-party-freeform-btn"
                            >
                                Prospective Party
                            </button>
                        </div>
                    </div>
                    {form.party_mode === "existing" ? (
                        <SearchableSelect
                            options={clients.map((c) => ({ value: c.id, label: c.name, hint: c.gstin || undefined }))}
                            value={form.client_id}
                            onChange={(v) => setForm((f) => ({ ...f, client_id: v }))}
                            placeholder="Pick an existing client…"
                            searchPlaceholder="Search clients…"
                            emptyText="No matching client."
                            testId="nda-new-client-picker"
                        />
                    ) : (
                        <div className="grid grid-cols-2 gap-3 mt-1">
                            <div>
                                <Label>Party Name *</Label>
                                <Input value={form.party_name} onChange={(e) => setForm((f) => ({ ...f, party_name: e.target.value }))} data-testid="nda-freeform-name" />
                            </div>
                            <div>
                                <Label>Legal Name</Label>
                                <Input value={form.party_legal_name} onChange={(e) => setForm((f) => ({ ...f, party_legal_name: e.target.value }))} data-testid="nda-freeform-legal-name" />
                            </div>
                            <div>
                                <Label>Email</Label>
                                <Input value={form.p_email} onChange={(e) => setForm((f) => ({ ...f, p_email: e.target.value }))} data-testid="nda-freeform-email" />
                            </div>
                            <div>
                                <Label>Phone</Label>
                                <Input value={form.p_phone} onChange={(e) => setForm((f) => ({ ...f, p_phone: e.target.value }))} data-testid="nda-freeform-phone" />
                            </div>
                            <div className="col-span-2">
                                <Label>Address</Label>
                                <Input value={form.p_address1} onChange={(e) => setForm((f) => ({ ...f, p_address1: e.target.value }))} data-testid="nda-freeform-address" />
                            </div>
                            <div>
                                <Label>City</Label>
                                <Input value={form.p_city} onChange={(e) => setForm((f) => ({ ...f, p_city: e.target.value }))} data-testid="nda-freeform-city" />
                            </div>
                            <div>
                                <Label>State</Label>
                                <Input value={form.p_state} onChange={(e) => setForm((f) => ({ ...f, p_state: e.target.value }))} data-testid="nda-freeform-state" />
                            </div>
                            <div>
                                <Label>Country</Label>
                                <Input value={form.p_country} onChange={(e) => setForm((f) => ({ ...f, p_country: e.target.value }))} data-testid="nda-freeform-country" />
                            </div>
                            <div>
                                <Label>PIN</Label>
                                <Input value={form.p_pin} onChange={(e) => setForm((f) => ({ ...f, p_pin: e.target.value }))} data-testid="nda-freeform-pin" />
                            </div>
                        </div>
                    )}
                </div>

                <div className="col-span-2">
                    <Label>Purpose (optional — defaults from template)</Label>
                    <Textarea
                        rows={2}
                        value={form.purpose}
                        onChange={(e) => setForm((f) => ({ ...f, purpose: e.target.value }))}
                        placeholder="Leave blank to use the template's default purpose statement."
                        data-testid="nda-new-purpose"
                    />
                </div>
                <div className="col-span-2">
                    <Label>Internal Remarks</Label>
                    <Input value={form.remarks} onChange={(e) => setForm((f) => ({ ...f, remarks: e.target.value }))} data-testid="nda-new-remarks" />
                </div>
            </div>
            <DialogFooter>
                <FormDialogCancel data-testid="nda-new-cancel">Cancel</FormDialogCancel>
                <Button onClick={submit} disabled={submitting} data-testid="nda-new-submit" className="bg-slate-900 hover:bg-slate-800 text-white">
                    {submitting ? "Creating…" : "Create NDA"}
                </Button>
            </DialogFooter>
        </FormDialog>
    );
}

export default function NDAs() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [clients, setClients] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState("");
    const [typeFilter, setTypeFilter] = useState("");
    const [clientFilter, setClientFilter] = useState("");
    const [textQuery, setTextQuery] = useState("");
    const [mine, setMine] = useState(false);
    const [newOpen, setNewOpen] = useState(false);

    const load = async () => {
        try {
            const params = {};
            if (statusFilter) params.status = statusFilter;
            if (typeFilter) params.nda_type = typeFilter;
            if (clientFilter) params.client_id = clientFilter;
            if (mine) params.mine = mine;
            const [nr, cls, tpls] = await Promise.all([
                api.get("/billing/nda/list", { params }),
                api.get("/billing/client/list"),
                api.get("/billing/nda-template/list"),
            ]);
            setItems(nr.data.items || []);
            setClients(cls.data.items || []);
            setTemplates(tpls.data.items || []);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load NDAs"));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, [statusFilter, typeFilter, clientFilter, mine]);

    const visibleItems = useMemo(() => {
        const q = textQuery.trim().toLowerCase();
        if (!q) return items;
        return items.filter((n) => {
            const num = (n.nda_number || "").toLowerCase();
            const party = (n.party_snapshot?.party_display_name || n.party_snapshot?.party_name || "").toLowerCase();
            const subj = (n.subject || "").toLowerCase();
            return num.includes(q) || party.includes(q) || subj.includes(q);
        });
    }, [items, textQuery]);

    return (
        <div data-testid="ndas-page">
            <PageHeader
                eyebrow="Sales · Pre-Commercial"
                title="NDAs"
                description="Non-Disclosure Agreements for evaluation and exploration of potential business opportunities.  Not a commercial contract; no billing, licence or production entitlement is created here."
                actions={
                    <RoleGuard permission="nda:write">
                        <div className="flex items-center gap-2" data-testid="nda-actions">
                            <Button
                                onClick={() => setNewOpen(true)}
                                className="bg-slate-900 hover:bg-slate-800 text-white gap-1.5"
                                data-testid="new-nda-btn"
                            >
                                <Plus size={16} weight="bold" /> New NDA
                            </Button>
                        </div>
                    </RoleGuard>
                }
            />
            <div className="px-6 sm:px-8 py-8">
                <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="nda-filters">
                    <div className="relative flex-1 min-w-[220px] max-w-md">
                        <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input
                            value={textQuery}
                            onChange={(e) => setTextQuery(e.target.value)}
                            placeholder="Search by NDA #, party or subject…"
                            className="pl-8 h-9 text-sm"
                            data-testid="nda-search-input"
                        />
                    </div>
                    <div className="reedos-eyebrow flex items-center gap-1.5"><Funnel size={12} /> Filter</div>
                    <select
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                        className="h-9 px-3 text-sm border border-slate-200 rounded-md bg-white"
                        data-testid="nda-type-filter"
                    >
                        <option value="">All types</option>
                        {NDA_TYPES.map((t) => <option key={t} value={t}>{t} NDA</option>)}
                    </select>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="h-9 px-3 text-sm border border-slate-200 rounded-md bg-white"
                        data-testid="nda-status-filter"
                    >
                        <option value="">All status</option>
                        {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <div className="w-56">
                        <SearchableSelect
                            options={[{ value: "", label: "All clients" }, ...clients.map((c) => ({ value: c.id, label: c.name }))]}
                            value={clientFilter}
                            onChange={setClientFilter}
                            placeholder="All clients"
                            searchPlaceholder="Filter clients…"
                            emptyText="No matching client."
                            testId="nda-client-filter"
                            className="!h-9"
                        />
                    </div>
                    <OwnershipToggle value={mine} onChange={setMine} testId="ndas-ownership-toggle" />
                </div>

                {loading ? (
                    <div className="text-sm text-slate-500">Loading NDAs…</div>
                ) : visibleItems.length === 0 ? (
                    <EmptyState
                        icon={<Shield size={40} weight="thin" />}
                        title="No NDAs yet"
                        description="Create a Data or Software NDA to protect information exchanged while exploring a potential engagement."
                        data-testid="ndas-empty"
                    />
                ) : (
                    <div className="reedos-card overflow-hidden">
                        <table className="w-full text-sm" data-testid="ndas-table">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr className="text-left">
                                    <th className="px-5 py-3 reedos-eyebrow">NDA #</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Type</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Party</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Subject</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Effective</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Valid Until</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Status</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Owner</th>
                                    <th className="px-5 py-3"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleItems.map((n) => {
                                    const badge = STATUS_BADGE_MAP[n.status] || { status: n.status };
                                    const party = n.party_snapshot?.party_display_name || n.party_snapshot?.party_name || "—";
                                    return (
                                        <tr key={n.id} className="border-t border-slate-100 reedos-row-hover" data-testid={`nda-row-${n.id}`}>
                                            <td className="px-5 py-3">
                                                <span className="inline-flex items-center flex-wrap gap-1.5">
                                                    <Link to={`/ndas/${n.id}`} className="font-mono text-xs font-semibold text-slate-900 hover:underline">
                                                        {n.nda_number}
                                                    </Link>
                                                    <CopyCodeButton
                                                        code={n.nda_number}
                                                        label="NDA number"
                                                        testid={`nda-number-copy-${n.id}`}
                                                    />
                                                </span>
                                            </td>
                                            <td className="px-5 py-3 text-slate-800 text-xs font-semibold uppercase tracking-wide">{n.nda_type}</td>
                                            <td className="px-5 py-3 text-slate-900">{party}</td>
                                            <td className="px-5 py-3 text-slate-700 text-xs">{n.subject}</td>
                                            <td className="px-5 py-3 text-xs text-slate-600">{formatDate(n.effective_date)}</td>
                                            <td className="px-5 py-3 text-xs text-slate-600">{formatDate(n.valid_until)}</td>
                                            <td className="px-5 py-3">
                                                <StatusBadge {...badge}>{n.status}</StatusBadge>
                                            </td>
                                            <td className="px-5 py-3">
                                                <OwnerCell owner={n.owner} testId={`nda-owner-${n.id}`} />
                                            </td>
                                            <td className="px-5 py-3 text-right">
                                                <Link
                                                    to={`/ndas/${n.id}`}
                                                    data-testid={`nda-open-${n.id}`}
                                                    className="text-slate-700 hover:text-slate-900 inline-flex items-center gap-1 text-xs font-medium"
                                                >
                                                    Open <ArrowRight size={12} weight="bold" />
                                                </Link>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            <NewNDADialog
                clients={clients}
                templates={templates}
                open={newOpen}
                onOpenChange={setNewOpen}
                onCreated={(n) => navigate(`/ndas/${n.id}`)}
            />
        </div>
    );
}
